/**
 * Chat event catalog — the typed mirror of `espotz-mobile-app/src/shared/analytics/events.ts`
 * (`ChatEvents`). Both platforms share one PostHog project, so the property names below must
 * stay byte-identical to the mobile ones or the cross-platform insights split in two.
 *
 * All events fire as `feature_usage` with `feature: 'chat'`, which web fans out to
 * PostHog + GA4 + Clarity via `trackFeatureUsage`.
 */

import { trackFeatureUsage } from '@/analytics/AnalyticsTracker';
import { track } from '@/analytics/eventTracker';
import { ConversationType } from '@/types/chat';

export type ChatType = 'tournament' | 'match';

/** Where a chat opening is recorded in the first-party events. */
const CHAT_SURFACE = {
  tournament: 'tournament_chat',
  match: 'match_chat',
} as const satisfies Record<ChatType, string>;
export type ChatFailReason =
  'too_long' | 'rate_limited' | 'chat_closed' | 'unknown';

/** Map the unified chat API's error codes to a bounded set of failure reasons. */
export function chatFailReasonFromCode(
  code: string | null | undefined,
): ChatFailReason {
  switch (code) {
    case 'CHAT_MESSAGE_INVALID':
      return 'too_long';
    case 'CHAT_RATE_LIMITED':
    case 'CHAT_SLOW_MODE':
      return 'rate_limited';
    case 'CHAT_CLOSED':
      return 'chat_closed';
    default:
      return 'unknown';
  }
}

/** The kind of chat as reported on unified-chat events (lowercase, same values as mobile). */
const CONVERSATION_KIND = {
  [ConversationType.DIRECT]: 'direct',
  [ConversationType.GROUP]: 'group',
  [ConversationType.TEAM]: 'team',
  [ConversationType.CLAN]: 'clan',
  [ConversationType.TOURNAMENT]: 'tournament',
  [ConversationType.MATCH]: 'match',
  [ConversationType.ORGANIZATION]: 'organization',
} as const satisfies Record<ConversationType, string>;
export type ChatConversationKind = (typeof CONVERSATION_KIND)[ConversationType];

export const chatConversationKind = (
  type: ConversationType,
): ChatConversationKind => CONVERSATION_KIND[type];

/** Where the user deleted a chat from. */
export enum ChatDeleteSource {
  INFO = 'info',
  INBOX = 'inbox',
}

/** Presence flag only — chat is user content, so the text itself is never sent. */
export function hasEmoji(text: string): boolean {
  return /\p{Extended_Pictographic}/u.test(text);
}

export const ChatEvents = {
  /** Chat entry point rendered for an eligible user. At most once per tournament per mount. */
  entryShown: (props: { tournament_id: string; surface: string }) =>
    trackFeatureUsage('chat', 'entry_shown', props),
  /**
   * Drawer opened (web) / chat screen mounted (mobile). Also recorded as the first-party
   * `chat.opened` event (Athena), which is what sponsors' per-tournament chat reach counts.
   */
  opened: (props: {
    tournament_id: string;
    chat_type: ChatType;
    source: string;
  }) => {
    trackFeatureUsage('chat', 'opened', props);
    if (props.tournament_id) {
      track('chat.opened', {
        entities: { tournamentId: props.tournament_id },
        surface: CHAT_SURFACE[props.chat_type],
      });
    }
  },
  /** Chat left. Counters are per-visit, so they reset on every `opened`. */
  closed: (props: {
    tournament_id: string;
    chat_type: ChatType;
    duration_ms: number;
    messages_sent: number;
    older_pages_loaded: number;
  }) => trackFeatureUsage('chat', 'closed', props),
  /** Send emitted to the socket. There is no per-message ack, so this is the send signal. */
  messageSent: (props: {
    tournament_id: string;
    chat_type: ChatType;
    length: number;
    has_emoji: boolean;
    /** Tournament chat only — match chat has no presence channel. */
    online_count?: number;
    sender_role?: string;
  }) => trackFeatureUsage('chat', 'message_sent', props),
  /** Server rejected the send, or the client-side length guard did. */
  messageFailed: (props: {
    tournament_id: string;
    chat_type: ChatType;
    reason: ChatFailReason;
    length: number;
  }) => trackFeatureUsage('chat', 'message_failed', props),
  /** An older history page was requested. `page_no` is the nth page loaded this visit. */
  historyOlderLoaded: (props: { tournament_id: string; page_no: number }) =>
    trackFeatureUsage('chat', 'history_older_loaded', props),
  /**
   * Participants / people panel opened. Unified chat fires it once the panel's first "who's
   * online" answer arrives, so `online_count` is real; it is left out when that fetch failed.
   */
  participantsOpened: (props: {
    tournament_id: string;
    chat_type: ChatType;
    online_count?: number;
  }) => trackFeatureUsage('chat', 'participants_opened', props),
  /** Quick-emoji picker opened. WEB-ONLY — mobile uses the OS keyboard. */
  emojiPickerOpened: (props: { tournament_id: string }) =>
    trackFeatureUsage('chat', 'emoji_picker_opened', props),
  /** Chat flipped to read-only while open. */
  readOnlyShown: (props: { tournament_id: string }) =>
    trackFeatureUsage('chat', 'read_only_shown', props),
  /** Socket dropped while chat was open. Edge-triggered — the initial disconnected state is not a loss. */
  connectionLost: (props: { tournament_id: string }) =>
    trackFeatureUsage('chat', 'connection_lost', props),
  /** A chat was deleted for the user (hidden, history cleared for them only). Fires on success. */
  conversationDeleted: (props: {
    conversation_type: ChatConversationKind;
    source: ChatDeleteSource;
  }) => trackFeatureUsage('chat', 'conversation_deleted', props),
};
